/*
 * Copyright (C) 2026 - present by Potentially
 *
 * Please see distribution for license.
 */

import { formatDate } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

export type PtlDateFormat =
  | 'date'
  | 'dateLong'
  | 'dateTime'
  | 'shortDateTime'
  | 'monthDay'
  | 'month'
  | 'monthYear'
  | 'monthYearLong'
  | 'time';

const LOCALE_BY_LANG: Record<string, string> = {
  en: 'en-GB',
  ar: 'ar',
  cy: 'cy',
  de: 'de',
  fr: 'fr',
};

export function formatPtlDate(value: string | number | Date, format: PtlDateFormat, lang: string): string {
  const locale = LOCALE_BY_LANG[lang] ?? LOCALE_BY_LANG['en'];
  const monthDayPattern = lang === 'en' ? 'MMM d' : 'd MMM';
  try {
    switch (format) {
      case 'date':
        return formatDate(value, 'd MMM y', locale);
      case 'dateLong':
        return formatDate(value, 'd MMMM y', locale);
      case 'dateTime':
        return `${formatDate(value, 'd MMM y', locale)}, ${formatDate(value, 'HH:mm', locale)}`;
      case 'shortDateTime':
        return `${formatDate(value, 'd MMM', locale)}, ${formatDate(value, 'HH:mm', locale)}`;
      case 'monthDay':
        return formatDate(value, monthDayPattern, locale);
      case 'month':
        return formatDate(value, 'MMM', locale);
      case 'monthYear':
        return formatDate(value, 'MMM yyyy', locale);
      case 'monthYearLong':
        return formatDate(value, 'MMMM yyyy', locale);
      case 'time':
        return formatDate(value, 'HH:mm', locale);
    }
  } catch {
    return '';
  }
}

@Pipe({
  name: 'ptlDate',
  standalone: true,
})
export class PtlDatePipe implements PipeTransform {
  private transloco = inject(TranslocoService);

  transform(value: string | number | Date | null | undefined, format: PtlDateFormat = 'date'): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return formatPtlDate(value, format, this.transloco.getActiveLang());
  }
}
